import {getReactSelectDefaultProps, ReactSelectProps} from "config/react-select";
import _ from 'lodash';
import {InputType} from "module/CustomField/util/CFInputType";
import React, {FocusEventHandler, KeyboardEventHandler, useCallback, useEffect, useState} from 'react';
import Select from 'react-select';
import {CF} from 'module/CustomField/util/CustomField';
import {log} from 'common/log';
import {CFComponentProps} from 'module/CustomField/type/CFComponentType';
import {CFValue} from 'module/CustomField/type/CFType';
import {focusNextFormElement} from 'util/view/focusNextFormElement'
import {DqElement, isDqSearchable} from 'module/Dq/searchDq'
import {useDqSearchRx} from 'module/CustomField/component/input/hook/useDqSearchRx'
import {CustomFieldSelectOption} from 'module/CustomField/component/input/ReactSelectComponent/CustomFieldSelectOption'
import {ReactSelectSingleValue} from 'module/CustomField/component/input/ReactSelectComponent/ReactSelectSingleValue'
import {filterOptionReactSelect} from 'module/CustomField/component/input/ReactSelectComponent/FilterOption'
import {GroupBase} from 'react-select/dist/declarations/src/types'
import {useMemoDq} from 'module/Memo/hook/useMemoDq'
import {ntes} from 'module/CustomField/component/util/ntes'
import {css} from '@emotion/react'

const cssPlaceholder = css`
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	max-width: calc(100% - 3px);
	font-size: small;
`

export interface CustomFieldSelectComponentCustomProps {
	isFocused?: boolean
	isAze?: boolean
	dispatchMemoToggleAction?: (x?: DqElement) => void
}

export function CustomFieldSelectComponent(props: CFComponentProps<CFValue>) {
	const {
		cfDef,
		setCb,
		setAzeText,
		model,
		modelValue,
		disabled,
		placeholderHTML,
		isAze = false,
		className,
	} = props

	const {
		searchSubject,
		suggestions,
		labelCache,
		loading,
	} = useDqSearchRx(props)
	const [menuOpen, setMenuOpen] = useState(false)
	const [viewValue, _setViewValue] = useState(ntes(modelValue))
	const setViewValue = useCallback((value) => _setViewValue(ntes(value)), [])
	const [focused, setFocused] = useState(false)
	const {memoElements, dispatchMemoAutoAddAction, dispatchMemoToggleAction} = useMemoDq({cfDef, modelValue, inputValue: viewValue})

	const {dqKey} = cfDef
	let inputType = InputType[cfDef.input as InputType || InputType.String]
	const isSelect = InputType.Dq === inputType

	const label = labelCache[modelValue as string] || viewValue
	let valueObj: DqElement = {value: CF.transformForView(cfDef, viewValue), label: label}
	let exactMatch = suggestions.find(x => x.value === valueObj.value)
	let options = memoElements.concat(suggestions.filter(x => !memoElements.some(dx => dx.value == x.value)))
	if (exactMatch) {
		if (memoElements.some(m => m.value == valueObj.value)) exactMatch = {...exactMatch, memo: true}
		options = [exactMatch, ...options.filter(x => x.value !== valueObj.value)]
	} else if (!exactMatch) {
		// add a invisible none-select value
		options = [{
			value: viewValue,
			label: '',
			_specialFirstEntry: true,
		}, ...options]
	}
	let value: DqElement | null = exactMatch || valueObj
	if (!value || !value.value && !value.label) value = null

	useEffect(() => {
		setViewValue(modelValue)
		setFocused(false)
	}, [modelValue])

	useEffect(() => {
		if (focused) {
			searchSubject.next({searchTerm: viewValue, model})
		} else {
			searchSubject.next({searchTerm: modelValue, model})
		}
	}, [searchSubject, model, modelValue, viewValue, focused])

	const onFocus: FocusEventHandler<HTMLInputElement> = useCallback((evt) => {
		const {target} = evt;
		setFocused(true);
		setTimeout(() => {
			if (document.activeElement === target) target.select()
		}, 20);
	}, [])

	// debounce and cancel is a fix for mobile devices
	// -> onBlur called immediate on mobile devices and therefore clears value on select from list
	const onBlur = _.debounce(() => {
		setFocused(false)
		setViewValue(modelValue) // refresh to model-value to prevent input from keeping wrong values
		if (!disabled) {
			setCb(viewValue)
		}
	}, 80)


	const onKeyDown: KeyboardEventHandler = useCallback((evt) => {
		if (evt.key === 'Escape') setViewValue(modelValue)
	}, [modelValue, setViewValue])

	const onChange: ReactSelectProps['onChange'] = (evt: { value?: string | number, label: string, azetext?: string }, {action}) => {
		if (disabled) return;
		switch (action) {
			case 'select-option':
				const {value, azetext} = evt
				dispatchMemoAutoAddAction(evt)
				setViewValue(value)
				setCb(value)
				if (azetext && setAzeText) {
					setAzeText({bookingNr: model.bookingNr, azetext: azetext})
				}
				setFocused(false)
				focusNextFormElement()
				onBlur.cancel()
				break;
			case 'deselect-option':
			case 'clear':
				setViewValue(null);
				setCb(null);
				break;
			case 'create-option':
			case 'remove-value':
			case 'pop-value':
				//nothing (multi value)
				break;
			default:
				log.error('unknown action')
		}
	}

	const onInputChange = useCallback((inputValue, {action}) => {
		const {length} = cfDef;
		if (inputValue && inputType === InputType.String && inputValue.length > length) {
			// disable longer inputs for inputs, where no 'search value' is used
			inputValue = (inputValue + '').substr(0, length);
		}
		switch (action) {
			case 'set-value':
				break;
			case 'input-change':
				setViewValue(inputValue);
				break;
			case 'input-blur':
			case 'menu-close':
				// this.setViewValue(this.props.modelValue);
				break;
			default:
				log.error('noop', action);
		}
	}, [cfDef, inputType])

	const onMenuOpen = useCallback(() => setMenuOpen(true), [])
	const onMenuClose = useCallback(() => setMenuOpen(false), [])

	let optionalInputValue;
	if (!dqKey || focused) {
		optionalInputValue = {inputValue: viewValue}
	}
	return (
		<div className={'CustomFieldSelectComponent'}>
			<Select
				{...getReactSelectDefaultProps<DqElement, false, GroupBase<DqElement>, CustomFieldSelectComponentCustomProps>({
					className,
					components: {
						SingleValue: ReactSelectSingleValue,
						DropdownIndicator: undefined, // click on dropdown indicator results in invisible value!
						Option: CustomFieldSelectOption,
					},
					menuIsOpen: isSelect && menuOpen,
					...optionalInputValue,
					isDisabled: disabled,
					...(placeholderHTML && !value ? {placeholder: <div css={cssPlaceholder} dangerouslySetInnerHTML={{__html: placeholderHTML}}/>} : {placeholder: ' '}),
					isClearable: false,
					options: options,
					isLoading: loading,
					isFocused: focused,
					value: value,
					onFocus: onFocus,
					onBlur: onBlur,
					onChange: onChange,
					onInputChange: onInputChange,
					onMenuOpen: onMenuOpen,
					onMenuClose: onMenuClose,
					onKeyDown: onKeyDown,
					filterOption: isDqSearchable(dqKey) ? null : filterOptionReactSelect,
					isAze: isAze,
					dispatchMemoToggleAction,
					tabSelectsValue: true,
				})}
			/>
		</div>
	)
}